import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../store";
import { fetchWalletTransactions } from "../../features/wallet/WalletServices";
import { getDataUserId } from "../../util/manageAuth";
import { FaChevronLeft, FaChevronRight, FaSearch } from "react-icons/fa";
import ECSLoader from "../../components/common/EcsLoader";
import { useDebounce } from "../../util/hooks/useDebounce";
import { WalletTransaction } from "../../types/paymentTypes";

const formatDate = (epoch: number) =>
    new Date(epoch).toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });

const dateToEpoch = (val: string) => (val ? new Date(val).getTime() : 0);

export const formatCurrency = (value?: number) => {
    if (value === null || value === undefined) return "0.00";

    return new Intl.NumberFormat("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(value);
};

export default function WalletTransactionList() {
    const dispatch = useDispatch<AppDispatch>();
    const userId = Number(getDataUserId());

    const [transactions, setTransactions] = useState<WalletTransaction[]>([]);
    const [pageInfo, setPageInfo] = useState<any>({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const [filters, setFilters] = useState({
        type: "" as "" | "CREDIT" | "DEBIT",
        fromDate: "",
        toDate: "",
        description: "",
        paymentId: ""
    });

    const [page, setPage] = useState(0);
    const debouncedFilters = useDebounce(filters, 500);

    useEffect(() => { setPage(0); }, [debouncedFilters]);

    const loadTransactions = async () => {
        if (!userId) return;
        setLoading(true);
        setError("");
        try {
            const result = await dispatch(
                fetchWalletTransactions({
                    page,
                    size: 10,
                    filters: {
                        userId,
                        ...(filters.type ? { type: filters.type } : {}),
                        ...(filters.paymentId ? { paymentId: filters.paymentId } : {}),
                        ...(filters.fromDate ? { fromDate: dateToEpoch(filters.fromDate) } : {}),
                        ...(filters.toDate ? { toDate: dateToEpoch(filters.toDate) + 86399999 } : {}),
                    },
                })
            ).unwrap();

            setTransactions(result.results || []);
            setPageInfo(result.page || {});
        } catch (err: any) {
            setError(err?.message || "Failed to load transactions");
            setTransactions([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadTransactions();
    }, [page, debouncedFilters]);

    const filteredTransactions = filters.description
        ? transactions.filter((t) =>
            t.description?.toLowerCase().includes(filters.description.toLowerCase())
        )
        : transactions;

    const totalPages = pageInfo.count ?? 0;
    const currentStart = pageInfo.index != null ? pageInfo.index * pageInfo.size + 1 : 1;
    const currentEnd = Math.min((pageInfo.index + 1) * pageInfo.size, pageInfo.total || 0);

    return (
        <div className="bg-white rounded-3xl p-4 sm:p-5 mt-5 w-full relative">
            <ECSLoader show={loading} />

            {error && (
                <div className="text-red-500 mb-3 text-sm font-medium">{error}</div>
            )}

            {/* Top Bar - Pagination */}
            <div className="flex justify-end items-center gap-2 mb-4">
                <div className="flex items-center gap-2 text-sm">
                    <span className="text-blue-500 font-semibold">
                        {currentStart}–{currentEnd}
                    </span>
                    <span className="text-gray-500">of {pageInfo.total || 0}</span>

                    <button
                        disabled={page === 0 || loading}
                        onClick={() => setPage((p) => p - 1)}
                        className="p-2 bg-gray-200 rounded-full disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-300 transition"
                    >
                        <FaChevronLeft size={14} />
                    </button>
                    <button
                        disabled={(totalPages > 0 && page + 1 >= totalPages) || loading}
                        onClick={() => setPage((p) => p + 1)}
                        className="p-2 bg-blue-500 text-white rounded-full disabled:opacity-40 disabled:cursor-not-allowed hover:bg-blue-600 transition"
                    >
                        <FaChevronRight size={14} />
                    </button>
                </div>
            </div>

            {/* Mobile Filters */}
            <div className="flex flex-wrap gap-2 mb-4 sm:hidden">
                <SearchBox
                    value={filters.description}
                    onChange={(v) => setFilters((prev) => ({ ...prev, description: v }))}
                    placeholder="Search description"
                />
                <select
                    value={filters.type}
                    onChange={(e) => setFilters((prev) => ({ ...prev, type: e.target.value as any }))}
                    className="border rounded-md h-8 px-2 text-xs font-medium outline-none focus:ring-1 focus:ring-blue-400"
                >
                    <option value="">All Types</option>
                    <option value="CREDIT">Credit</option>
                    <option value="DEBIT">Debit</option>
                </select>
                <input
                    type="date"
                    value={filters.fromDate}
                    max={filters.toDate || undefined}
                    onChange={(e) => setFilters((prev) => ({ ...prev, fromDate: e.target.value }))}
                    className="border rounded-md h-8 px-2 text-xs outline-none focus:ring-1 focus:ring-blue-400"
                />
                <input
                    type="date"
                    value={filters.toDate}
                    min={filters.fromDate || undefined}
                    onChange={(e) => setFilters((prev) => ({ ...prev, toDate: e.target.value }))}
                    className="border rounded-md h-8 px-2 text-xs outline-none focus:ring-1 focus:ring-blue-400"
                />
            </div>

            <div
                className={`overflow-x-auto transition-opacity duration-300 ${loading ? "opacity-40 pointer-events-none" : "opacity-100"
                    }`}
            >
                <table className="w-full table-fixed text-sm border-separate border-spacing-y-2">
                    <thead className="text-gray-600">

                        <tr>
                            <th className="p-2 text-left font-bold w-[6%]">ID</th>
                            <th className="p-2 text-left font-bold w-[16%]">Payment ID</th>
                            <th className="p-2 text-left font-bold w-[10%" >Amount</th>
                            <th className="p-2 text-left font-bold w-[10%">GST</th>
                            <th className="p-2 text-left font-bold w-[10%">Other Charges</th>
                            <th className="p-2 text-left font-bold w-[12%]">Type</th>
                            <th className="p-2 text-left font-bold w-[16%]">Date & Time</th>
                            <th className="p-2 text-left font-bold w-[16%]">Description</th>
                        </tr>

                        {/* Filter Row */}
                        <tr>
                            <th />
                            <th className="py-1">
                                <SearchBox
                                    value={filters.paymentId}
                                    onChange={(v) => setFilters((prev) => ({ ...prev, paymentId: v }))}
                                    placeholder="Search id"
                                />
                            </th>
                            <th />
                            <th />
                            <th />
                            <th className="py-1">
                                <select
                                    value={filters.type}
                                    onChange={(e) => setFilters((prev) => ({ ...prev, type: e.target.value as any }))}
                                    className="w-full border rounded-md h-8 px-2 text-xs font-medium outline-none focus:ring-1 focus:ring-blue-400"
                                >
                                    <option value="">All Types</option>
                                    <option value="CREDIT">Credit</option>
                                    <option value="DEBIT">Debit</option>
                                </select>
                            </th>
                            <th className="py-1">
                                <div className="flex gap-2">
                                    <input
                                        type="date"
                                        value={filters.fromDate}
                                        max={filters.toDate || undefined}
                                        onChange={(e) => setFilters((prev) => ({ ...prev, fromDate: e.target.value }))}
                                        className="flex-1 border rounded-md h-8 px-2 text-xs outline-none focus:ring-1 focus:ring-blue-400"
                                    />
                                    {/* <input
                                        type="date"
                                        value={filters.toDate}
                                        min={filters.fromDate || undefined}
                                        onChange={(e) => setFilters((prev) => ({ ...prev, toDate: e.target.value }))}
                                        className="flex-1 border rounded-md h-8 px-2 text-xs outline-none focus:ring-1 focus:ring-blue-400"
                                    /> */}
                                </div>
                            </th>
                            <th />
                        </tr>
                    </thead>

                    <tbody>
                        {filteredTransactions.length === 0 && !loading ? (
                            <tr>
                                <td colSpan={6} className="text-center py-8 text-gray-400">
                                    No transactions found.
                                </td>
                            </tr>
                        ) : (
                            filteredTransactions.map((item, index) => (
                                <tr
                                    key={item.id ?? index}
                                    className="font-medium even:bg-gray-50 odd:bg-white hover:bg-gray-100 transition-colors"
                                >
                                    <td className="p-2 text-gray-500">{item.id}</td>
                                    <td className="p-2 text-gray-500 break-all">
                                        {item.paymentId}
                                    </td>
                                    <td className="p-2 font-semibold">
                                        ₹{formatCurrency(item.amount)}
                                    </td>

                                    <td className="p-2 font-semibold">
                                        ₹{formatCurrency(item.gst)}
                                    </td>

                                    <td className="p-2 font-semibold">
                                        ₹{formatCurrency(item.razorpayCharges)}
                                    </td>
                                    <td className="p-2">
                                        <TypeBadge type={item.type} />
                                    </td>
                                    <td className="p-2 text-gray-500 text-xs">
                                        {formatDate(item.createdAt)}
                                    </td>
                                    <td className="p-2 truncate">
                                        {item.description}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

/* ── Helpers ─────────────────────────────────────────────────────────────── */

function TypeBadge({ type }: { type: string }) {
    const isCredit = type === "CREDIT";
    return (
        <span
            className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full ${isCredit ? "bg-green-50 text-green-600" : "bg-red-50 text-red-500"
                }`}
        >
            <span
                className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${isCredit ? "bg-green-500" : "bg-red-500"
                    }`}
            />
            {type}
        </span>
    );
}

function SearchBox({
    value = "",
    onChange,
    placeholder = "Search",
}: {
    value?: string;
    onChange?: (val: string) => void;
    placeholder?: string;
}) {
    return (
        <div className="relative w-[70%] max-w-[200px]">
            <input
                value={value}
                onChange={(e) => onChange?.(e.target.value)}
                type="text"
                placeholder={placeholder}
                className="w-full border rounded-md h-8 pl-8 pr-2 text-xs font-medium outline-none focus:ring-1 focus:ring-blue-400"
            />
            <FaSearch className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none" />
        </div>
    );
}